import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const TIER_COLORS = {
  TRIAL: { bg: '#fef3c7', color: '#92400e' },
  FREEWARE: { bg: '#f3f4f6', color: '#374151' },
  BASIC: { bg: '#d1fae5', color: '#059669' },
  STARTER: { bg: '#d1fae5', color: '#059669' },
  ADVANCED: { bg: '#dbeafe', color: '#2563eb' },
  PRO: { bg: '#dbeafe', color: '#2563eb' },
  ENTERPRISE: { bg: '#ede9fe', color: '#7c3aed' },
};

export default function AdminOrganization() {
  const { adminEmail } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [showPlans, setShowPlans] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => { loadOrg(); }, []);

  async function loadOrg() {
    try {
      const res = await fetch('/api/organization/my-org', { headers: { 'x-admin-email': adminEmail } });
      const data = await res.json();
      setOrg(data.organization);
      setForm({
        name: data.organization?.name || '',
        email: data.organization?.email || '',
        phone: data.organization?.phone || '',
        address: data.organization?.address || '',
        city: data.organization?.city || '',
        state: data.organization?.state || '',
        zip: data.organization?.zip || '',
        website: data.organization?.website || ''
      });
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }

  async function saveOrg() {
    setSaving(true);
    try {
      const res = await fetch(`/api/organization/${org.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      alert('Organization updated!');
      loadOrg();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally { setSaving(false); }
  }

  async function openPlans() {
    try {
      const res = await fetch('/api/subscription/plans');
      const data = await res.json();
      setPlans(data.plans || []);
    } catch (e) { console.error(e); }
    setShowPlans(true);
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  const tierColor = TIER_COLORS[org?.tier] || TIER_COLORS.BASIC;
  const inputStyle = { width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 20 }}>
      <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 900 }}>🏢 Organization Settings</h1>
      <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>Manage your organization profile and subscription</p>

      {/* Subscription Status Card */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Current Plan</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 24, fontWeight: 900, color: tierColor.color }}>{org?.tier}</span>
              <span style={{
                padding: '4px 10px', borderRadius: 12,
                background: org?.subscriptionStatus === 'ACTIVE' ? '#d1fae5' : '#fef3c7',
                color: org?.subscriptionStatus === 'ACTIVE' ? '#065f46' : '#92400e',
                fontSize: 12, fontWeight: 600
              }}>
                {org?.subscriptionStatus}
              </span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900 }}>{org?.userCount || 0}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>/ {org?.userLimit} Users</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900 }}>{org?.loadsThisMonth || 0}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>/ {(org?.monthlyLoadLimit || 0).toLocaleString()} Loads</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900 }}>
                {org?.monthlyPrice ? `$${(org.monthlyPrice / 100).toFixed(0)}` : 'Custom'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>/ month</div>
            </div>
          </div>
        </div>

        {/* Features */}
        {org?.features && (
          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(org.features).map(([key, val]) => (
              <span key={key} style={{
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                background: val ? '#d1fae5' : '#f3f4f6',
                color: val ? '#065f46' : '#9ca3af',
              }}>
                {val ? '✓' : '✗'} {key.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Organization Details */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 800 }}>Organization Details</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div><label style={labelStyle}>Organization Name *</label><input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
          <div><label style={labelStyle}>Email *</label><input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /></div>
          <div><label style={labelStyle}>Phone</label><input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} /></div>
          <div><label style={labelStyle}>Website</label><input value={form.website || ''} onChange={e => setForm({ ...form, website: e.target.value })} style={inputStyle} /></div>
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={labelStyle}>Address</label>
          <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginTop: 16 }}>
          <div><label style={labelStyle}>City</label><input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} style={inputStyle} /></div>
          <div><label style={labelStyle}>State</label><input value={form.state || ''} onChange={e => setForm({ ...form, state: e.target.value })} style={inputStyle} /></div>
          <div><label style={labelStyle}>ZIP</label><input value={form.zip || ''} onChange={e => setForm({ ...form, zip: e.target.value })} style={inputStyle} /></div>
        </div>
        <button onClick={saveOrg} disabled={saving} style={{
          marginTop: 20, padding: '12px 24px', background: saving ? '#9ca3af' : '#DC2626',
          color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer'
        }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={async () => {
          try {
            const res = await fetch('/api/subscription/create-portal', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
              body: JSON.stringify({ baseUrl: window.location.origin })
            });
            const data = await res.json();
            if (data.portalUrl) window.location.href = data.portalUrl;
            else alert(data.detail || 'Error opening billing portal');
          } catch (e) { alert('Error: ' + e.message); }
        }} style={{
          marginTop: 20, marginLeft: 12, padding: '12px 24px', background: '#7c3aed',
          color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer'
        }}>
          💳 Manage Billing
        </button>
      </div>

      {/* Upgrade Card */}
      {org?.tier !== 'ENTERPRISE' && (
        <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)', borderRadius: 12, padding: 24, color: 'white' }}>
          <h3 style={{ margin: '0 0 8px 0' }}>🚀 Upgrade Your Plan</h3>
          <p style={{ margin: '0 0 16px 0', opacity: 0.9 }}>Get more loads, users, and features with our Professional or Enterprise plans.</p>
          <button onClick={openPlans} style={{
            padding: '10px 20px', background: 'white', color: '#7c3aed',
            border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer'
          }}>
            View Plans
          </button>
        </div>
      )}

      {/* Plans Modal */}
      {showPlans && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 900, width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>📋 Available Plans</h2>
              <button onClick={() => setShowPlans(false)} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f3f4f6', cursor: 'pointer', fontWeight: 700 }}>✕ Close</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, 1fr)`, gap: 16 }}>
              {plans.map(p => {
                const isCurrent = p.plan.toUpperCase() === org?.tier;
                return (
                  <div key={p.plan} style={{
                    border: isCurrent ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    borderRadius: 12, padding: 20, background: isCurrent ? '#eff6ff' : 'white',
                    display: 'flex', flexDirection: 'column'
                  }}>
                    {isCurrent && <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', marginBottom: 8 }}>✓ CURRENT PLAN</div>}
                    <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>{p.label}</div>
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#111827', marginBottom: 4 }}>
                      {p.priceMonthlyCents ? `$${(p.priceMonthlyCents / 100).toFixed(0)}` : p.plan === 'trial' ? 'Free' : 'Custom'}
                      {p.priceMonthlyCents && <span style={{ fontSize: 14, fontWeight: 400, color: '#6b7280' }}>/mo</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, flex: 1 }}>
                      <div style={{ marginBottom: 4 }}>👤 {p.maxUsers} users</div>
                      <div style={{ marginBottom: 4 }}>📦 {p.maxShipmentsPerMonth?.toLocaleString()} loads/mo</div>
                      <div style={{ marginBottom: 4 }}>🔗 {p.maxCarrierIntegrations} carrier integrations</div>
                      {p.features && Object.entries(p.features).filter(([,v]) => v).map(([k]) => (
                        <div key={k} style={{ marginBottom: 2 }}>✅ {k.replace(/_/g, ' ')}</div>
                      ))}
                    </div>
                    {!isCurrent && (
                      <button style={{
                        padding: '10px', background: '#2563eb', color: 'white',
                        border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', width: '100%'
                      }}
                        onClick={async () => {
                        if (!p.stripePriceId) {
                          alert('Contact sales for Enterprise plan: sales@roadoptix.com');
                          return;
                        }
                        try {
                          const res = await fetch('/api/subscription/create-checkout', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
                            body: JSON.stringify({ plan: p.plan, baseUrl: window.location.origin })
                          });
                          const data = await res.json();
                          if (data.checkoutUrl) window.location.href = data.checkoutUrl;
                          else alert(data.detail || 'Error creating checkout');
                        } catch (e) { alert('Error: ' + e.message); }
                      }}
                      >
                        {p.plan === 'enterprise' ? 'Contact Sales' : 'Upgrade'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
